import type { GroupedOption } from '@ksyos/design-system';
import { Box, FormGroup, Heading, Radio, RadioGroup, Select } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IOption, IService } from '@frontend/components/interfaces';
import type { ValidationErrors } from '@frontend/utils';
import { ChoiceWithEpisodeCategory } from '@common/validation/schemas/choiceSchema';
import { getSelectedLanguage } from '@frontend/translate';
import {
    getServicesGroupedByEpisodeCategoriesOptions,
    getValidationErrorTranslation,
    useAbortableFetch,
} from '@frontend/utils';
import { getSelectArrayValueObject } from '@frontend/utils/getSelectObject';
import type { ISearchParams } from '../requestFinancialExportUtils';

interface IRequestFinancialExportServiceFilterProps {
    errors: ValidationErrors;
    isDisabled: boolean;
    onChange: (filterName: keyof ISearchParams, value?: string) => void;
    searchParams: ISearchParams;
}

interface IFinancingEpisodeCategoryFilter {
    id: string;
    name: string;
}

export function RequestFinancialExportServiceFilter(props: IRequestFinancialExportServiceFilterProps) {
    const language = getSelectedLanguage();
    const abortableFetch = useAbortableFetch();
    const [serviceOptions, setServiceOptions] = useState<GroupedOption[]>([]);
    const [episodeCategoryOptions, setEpisodeCategoryOptions] = useState<IOption<string>[]>([]);

    useEffect(() => {
        loadServices();
        loadEpisodeCategoryFilters();
    }, []);

    const radioOptions = [
        { label: language.export.allServices, value: ChoiceWithEpisodeCategory.all },
        { label: language.episodeCategory.episodeCategory, value: ChoiceWithEpisodeCategory.episodeCategory },
        { label: language.export.specificService, value: ChoiceWithEpisodeCategory.specific },
    ];

    return (
        <>
            <Heading
                marginY={3}
                size="small"
            >
                {language.service.service}
            </Heading>
            <FormGroup
                hasErrors={props.errors.has('serviceId') || props.errors.has('episodeCategoryId')}
                isRequired={true}
                label={language.finance.chooseServices}
            >
                <RadioGroup
                    error={props.errors.get('serviceChoice')}
                    name={'serviceChoice'}
                    onChange={(event) => props.onChange('serviceChoice', event.target.value)}
                    value={props.searchParams.serviceChoice ?? ''}
                >
                    {radioOptions.map((item) => (
                        <Radio
                            disabled={props.isDisabled}
                            key={item.value}
                            label={item.label}
                            value={item.value}
                        />
                    ))}
                </RadioGroup>
                <Box marginTop={2}>
                    {props.searchParams?.serviceChoice === ChoiceWithEpisodeCategory.specific && (
                        <Select
                            error={getValidationErrorTranslation(props.errors.get('serviceId'))}
                            isClearable={true}
                            isDisabled={props.isDisabled}
                            isOptionDisabled={(option) => Boolean(option.isGroupHeader)}
                            onChange={(item) => props.onChange('serviceId', item?.value)}
                            options={serviceOptions}
                            placeholder={`${language.common.select} ${language.service.service}`}
                            value={
                                props.searchParams.serviceId
                                    ? serviceOptions.find((item) => item.value === props.searchParams.serviceId)
                                    : null
                            }
                        />
                    )}

                    {props.searchParams?.serviceChoice === ChoiceWithEpisodeCategory.episodeCategory && (
                        <Select
                            error={getValidationErrorTranslation(props.errors.get('episodeCategoryId'))}
                            isClearable={true}
                            isDisabled={props.isDisabled}
                            onChange={(item) => props.onChange('episodeCategoryId', item?.value)}
                            options={episodeCategoryOptions}
                            placeholder={`${language.common.select} ${language.episodeCategory.episodeCategory}`}
                            value={getSelectArrayValueObject(
                                props.searchParams?.episodeCategoryId,
                                episodeCategoryOptions
                            )}
                        />
                    )}
                </Box>
            </FormGroup>
        </>
    );

    async function loadServices() {
        const url = '/api/services?selectionList=true';
        const result = await abortableFetch.fetchJson(url, 'GET');
        if (result.json.items) {
            const options = result.json.items.sort((a: IService, b: IService) => {
                const valueA = language.episodeCategoryEnum[a.episodeCategory.name] + a.name;
                const valueB = language.episodeCategoryEnum[b.episodeCategory.name] + b.name;

                return valueA.localeCompare(valueB);
            });

            const optionsGroupedByEpisodeCategory = getServicesGroupedByEpisodeCategoriesOptions(options);
            setServiceOptions(optionsGroupedByEpisodeCategory);
        }
    }

    async function loadEpisodeCategoryFilters() {
        const url = '/api/episodeCategories?isActive=true';
        const result = await abortableFetch.fetchJson(url, 'GET');
        if (result.json.items) {
            let loadedEpisodeCategoryOptions: IOption<string>[] = result.json.items.map(
                (item: IFinancingEpisodeCategoryFilter) => {
                    return {
                        label: language.episodeCategoryEnum[item.name as keyof typeof language.episodeCategoryEnum],
                        value: item.id,
                    };
                }
            );

            loadedEpisodeCategoryOptions = loadedEpisodeCategoryOptions.sort(
                (a: IOption<string>, b: IOption<string>) => {
                    return a.label.localeCompare(b.label);
                }
            );

            setEpisodeCategoryOptions(loadedEpisodeCategoryOptions);
        }
    }
}
